import {
    ResponsiveContainer, AreaChart, Area, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell, Legend, ReferenceDot,
} from "recharts";
import { fmtCompact, fmtCurrency, fmtDate, gainClass } from "@/lib/format";
import { CHART_COLORS, TIME_RANGES } from "@/lib/constants";
import { usePrivacy } from "@/context/PrivacyContext";

export function RangeSelector({ value, onChange, ranges = TIME_RANGES, testPrefix = "range" }) {
    return (
        <div className="inline-flex flex-wrap gap-1 p-1 rounded-lg bg-muted/60" data-testid="range-selector">
            {ranges.map((r) => (
                <button key={r} onClick={() => onChange(r)}
                    data-testid={`${testPrefix}-${r.toLowerCase()}-btn`}
                    className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-colors num ${
                        value === r ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                    }`}>
                    {r}
                </button>
            ))}
        </div>
    );
}

function ChartTooltip({ active, payload, label, valuePrefix, hidden, bases }) {
    if (!active || !payload?.length) return null;
    return (
        <div className="rounded-lg border border-border bg-popover/95 backdrop-blur-sm px-3 py-2 shadow-xl text-xs">
            <div className="text-muted-foreground mb-1">{fmtDate(label)}</div>
            {payload.map((p) => {
                const base = bases?.[p.dataKey];
                const pct = (base && p.value != null) ? ((p.value / base - 1) * 100) : null;
                return (
                    <div key={p.dataKey} className="flex items-center gap-2 num">
                        <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
                        <span className="text-muted-foreground">{p.name}:</span>
                        <span className="font-semibold">{hidden ? "••••" : `${valuePrefix || ""}${fmtCurrency(p.value)}`}</span>
                        {!hidden && pct != null && (
                            <span className={`font-semibold ${gainClass(pct)}`}>
                                {`${pct >= 0 ? "+" : ""}${pct.toFixed(2)}%`}
                            </span>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

export function PerformanceChart({ series = [], benchmarks = null, height = 320 }) {
    const { hidden } = usePrivacy();
    // merge benchmarks by date
    const data = series.map((pt) => ({ date: pt.date, Portfolio: pt.value }));
    const benchNames = [];
    if (benchmarks) {
        const byDate = Object.fromEntries(data.map((d) => [d.date, d]));
        Object.entries(benchmarks).forEach(([name, arr]) => {
            if (!arr?.length) return;
            benchNames.push(name);
            arr.forEach((pt) => { if (byDate[pt.date]) byDate[pt.date][name] = pt.value; });
        });
    }
    if (!data.length) return <div className="h-[320px] flex items-center justify-center text-sm text-muted-foreground">No data for this range.</div>;

    // baseline (first non-null point) per series, for % change in the tooltip
    const bases = {};
    ["Portfolio", ...benchNames].forEach((k) => {
        const firstPt = data.find((d) => d[k] != null);
        if (firstPt) bases[k] = firstPt[k];
    });

    // intraday ranges have several points per day: put one tick on each day's first point
    const dayOf = (d) => String(d).slice(0, 10);
    const dayTicks = data.filter((d, i) => i === 0 || dayOf(d.date) !== dayOf(data[i - 1].date)).map((d) => d.date);
    const ticks = dayTicks.length < data.length ? dayTicks : undefined;

    return (
        <ResponsiveContainer width="100%" height={height}>
            <AreaChart data={data} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
                <defs>
                    <linearGradient id="pfGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                    </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.4} vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickLine={false} ticks={ticks}
                    axisLine={false} minTickGap={40} tickFormatter={(d) => fmtDate(d).replace(/,.*/, "")} />
                <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false}
                    width={56} tickFormatter={hidden ? () => "••" : fmtCompact} domain={["auto", "auto"]} />
                <Tooltip content={<ChartTooltip hidden={hidden} bases={bases} />} />
                <Area type="monotone" dataKey="Portfolio" stroke="hsl(var(--primary))" strokeWidth={2}
                    fill="url(#pfGrad)" dot={false} />
                {benchNames.map((name, i) => name.startsWith("Portfolio") ? (
                    <Area key={name} type="monotone" dataKey={name} stroke="#2563EB" strokeWidth={1.75} fill="transparent" dot={false} />
                ) : (
                    <Area key={name} type="monotone" dataKey={name} stroke={CHART_COLORS[(i + 1) % CHART_COLORS.length]}
                        strokeWidth={1.5} strokeDasharray="4 3" fill="transparent" dot={false} />
                ))}
                {benchNames.length > 0 && <Legend wrapperStyle={{ fontSize: 12 }} />}
            </AreaChart>
        </ResponsiveContainer>
    );
}

export function MultiLineChart({ dataKeys = [], data = [], height = 360, valueSuffix = "%", intraday = false, colors = CHART_COLORS, markers = [] }) {
    if (!data.length) return <div className="flex items-center justify-center text-sm text-muted-foreground" style={{ height }}>No data.</div>;
    const xf = intraday
        ? (d) => new Date(d).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
        : (d) => fmtDate(d).replace(/,.*/, "");
    const lf = intraday
        ? (d) => new Date(d).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
        : fmtDate;
    return (
        <ResponsiveContainer width="100%" height={height}>
            <LineChart data={data} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.4} vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickLine={false}
                    axisLine={false} minTickGap={40} tickFormatter={xf} />
                <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false}
                    width={48} tickFormatter={(v) => `${v.toFixed(0)}${valueSuffix}`} />
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 10, fontSize: 12 }}
                    formatter={(v) => `${v?.toFixed(2)}${valueSuffix}`} labelFormatter={lf} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {dataKeys.map((k, i) => (
                    <Line key={k} type="monotone" dataKey={k} stroke={colors[i % colors.length]}
                        strokeWidth={2} dot={false} />
                ))}
                {markers.map((m, i) => (
                    <ReferenceDot key={`mk-${i}`} x={m.x} y={m.y} ifOverflow="extendDomain" isFront
                        shape={(p) => (
                            <g style={{ cursor: "pointer" }} data-testid={`chart-marker-${m.label}-${i}`}>
                                <title>{m.tip}</title>
                                <circle cx={p.cx} cy={p.cy} r={6} fill={m.color} stroke="hsl(var(--card))" strokeWidth={1.5} />
                                <text x={p.cx} y={p.cy - 10} textAnchor="middle" fontSize={9} fontWeight={700} fill={m.color}>{m.label}</text>
                            </g>
                        )} />
                ))}
            </LineChart>
        </ResponsiveContainer>
    );
}

export function AllocationDonut({ data = [], height = 260, testId }) {
    if (!data.length) return <div className="flex items-center justify-center text-sm text-muted-foreground" style={{ height }}>No holdings.</div>;
    const outer = Math.max(48, Math.floor(height / 2) - 8);
    const inner = Math.floor(outer * 0.64);
    return (
        <ResponsiveContainer width="100%" height={height} data-testid={testId}>
            <PieChart>
                <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={inner} outerRadius={outer}
                    paddingAngle={2} stroke="hsl(var(--card))" strokeWidth={2}>
                    {data.map((entry, i) => <Cell key={entry.name ?? i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 10, fontSize: 12 }}
                    formatter={(v, n) => [fmtCurrency(v), n]} />
            </PieChart>
        </ResponsiveContainer>
    );
}

export function AllocationLegend({ data = [], total }) {
    return (
        <div className="space-y-2.5">
            {data.slice(0, 8).map((d, i) => (
                <div key={d.name ?? i} className="flex items-center gap-2.5 text-sm">
                    <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                    <span className="flex-1 truncate font-medium">{d.name}</span>
                    <span className="num text-muted-foreground">{(d.pct ?? 0).toFixed(1)}%</span>
                </div>
            ))}
        </div>
    );
}


export function SimpleAreaChart({ data = [], height = 380, axisFmt = (v) => v, tipFmt = (v) => v, color = "hsl(var(--primary))" }) {
    if (!data.length) return <div className="flex items-center justify-center text-sm text-muted-foreground" style={{ height }}>No data for this range.</div>;
    return (
        <ResponsiveContainer width="100%" height={height}>
            <AreaChart data={data} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
                <defs>
                    <linearGradient id="metricGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={color} stopOpacity={0.3} />
                        <stop offset="100%" stopColor={color} stopOpacity={0} />
                    </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.4} vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickLine={false}
                    axisLine={false} minTickGap={40} tickFormatter={(d) => fmtDate(d).replace(/,.*/, "")} />
                <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false}
                    width={64} tickFormatter={axisFmt} domain={["auto", "auto"]} />
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 10, fontSize: 12 }}
                    formatter={(v) => tipFmt(v)} labelFormatter={fmtDate} />
                <Area type="monotone" dataKey="value" stroke={color} strokeWidth={2} fill="url(#metricGrad)" dot={false} />
            </AreaChart>
        </ResponsiveContainer>
    );
}

export function MiniSparkline({ values = [], color = "hsl(var(--accent))", width = 68, height = 30 }) {
    if (!values || values.length < 2) return <div style={{ width, height }} className="opacity-40" />;
    const data = values.map((v, i) => ({ i, v }));
    return (
        <ResponsiveContainer width={width} height={height}>
            <LineChart data={data} margin={{ top: 3, right: 0, left: 0, bottom: 3 }}>
                <YAxis hide domain={["auto", "auto"]} />
                <Line type="monotone" dataKey="v" stroke={color} strokeWidth={1.5} dot={false} isAnimationActive={false} />
            </LineChart>
        </ResponsiveContainer>
    );
}

export function Sparkline({ data = [], dataKey = "vol", height = 90, color = "hsl(var(--accent))", suffix = "%" }) {
    if (!data.length) return <div className="text-xs text-muted-foreground flex items-center" style={{ height }}>No data.</div>;
    return (
        <ResponsiveContainer width="100%" height={height}>
            <LineChart data={data} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
                <YAxis hide domain={["auto", "auto"]} />
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 10, fontSize: 12 }}
                    formatter={(v) => `${v}${suffix}`} labelFormatter={fmtDate} />
                <Line type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2} dot={false} />
            </LineChart>
        </ResponsiveContainer>
    );
}
